<template>
  <div class="card card-white pending-approvals">
    <BaseDataTable
      :entries="tableData.rows"
      :columns="tableData.columns"
      :loading="loading"
      :paginator-info="paginatorInfo"
      class="f-12"
      force-search-in-api-mode
      @search="updatePagination"
    >
      <template #no-results="{ loading, search }">
        <WdsStack direction-row justify-center>
          <BaseLoadingNotice v-if="loading" class="tw-z-10">
            <template #main>{{ $t('Finding pending approvals') }}</template>
          </BaseLoadingNotice>
          <template v-else-if="!!search">
            <WdsStack class="tw-py-10" direction-column justify-center align-center gap-sm>
              <WdsText large>
                {{ $t('No Matching Results ') }}
              </WdsText>
              <WdsText secondary>
                {{
                  $t(
                    "We couldn't find any requests matching your search or filters. Please try adjusting your search terms or filter criteria.",
                  )
                }}
              </WdsText>
            </WdsStack>
          </template>
          <template v-else>
            <WdsStack class="tw-py-10" direction-column justify-center align-center gap-sm>
              <div class="icon-inner tw-w-fit">
                <WdsIcon name="checkmark" weight="thick" color="#5D36F1" :size="24"></WdsIcon>
              </div>
              <WdsText large>
                {{ $t('There are no pending approvals for your groups') }}
              </WdsText>
              <WdsText secondary>
                {{ $t('Change view to “Reviewed” to see past requests.') }}
              </WdsText>
            </WdsStack>
          </template>
        </WdsStack>
      </template>
      <template #control-bar="{ updateSearch, search }">
        <slot name="header" :update-search="updateSearch" :search="search"></slot>
      </template>
      <template #Reviewer="{ row }">
        <WdsTag>
          {{ latestStateFromApprovable(row).approver.userGroup.name }}
        </WdsTag>
      </template>
      <template #Action="{ row }">
        <WdsStack direction-row justify-end align-center gap-sm>
          <WdsButtonGhost secondary @click.prevent="openApprovalRequestModal(row)">
            {{ $t('Review') }}
          </WdsButtonGhost>
          <WdsMenu placement="bottom-end" global>
            <template #trigger>
              <WdsButtonGhost>
                <WdsIcon name="overflow-menu-horizontal" :size="16" />
              </WdsButtonGhost>
            </template>
            <WdsButtonGhost
              class="tw-mx-2"
              target="_blank"
              :href="
                laravelNamedRoute('hires.hire.view', {
                  hire: row?.approvable?.id,
                  tab: 'approvals',
                })
              "
              @click.stop
            >
              <WdsMenuOption>
                <WdsIcon :size="16" name="stamp" />
                {{ $t('View approval details') }}
              </WdsMenuOption>
            </WdsButtonGhost>
          </WdsMenu>
        </WdsStack>
      </template>
    </BaseDataTable>
  </div>

  <Teleport to="body">
    <ApprovalRequestModal
      v-if="approvableInReviewId"
      v-model:show="showApprovalRequestModal"
      :approval-approvable-id="approvableInReviewId"
      @refresh-approvables="reloadApprovalApprovables"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ComputedRef, markRaw, reactive, Ref, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsStack,
  WdsTag,
  WdsText,
} from '@worksome/wds-components'
import BaseDataTable from '@/components/BaseDataTable'
import { TableDataHandler } from '@/vue-prop-types/DataTable'
import { TdText } from '@/components/BaseDataTable/Cells'
import ApprovalRequestModal from '@/components/Workflows/Approvals/Request/ApprovalRequestModal.vue'
import { graphql, useFragment } from '@/gql'
import { useLocaleTimeAgo } from '@/composables/useLocaleTimeAgo'
import { PaginatorInfoFragment } from '@/components/GraphQL/fragments/paginatorInfoFragment'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import BaseLoadingNotice from '@/components/BaseLoadingNotice.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import ApprovalDisplayForTable from '@/components/Workflows/Approvals/Triggers/ApprovalDisplayForTable.vue'

const props = defineProps<{ accounts: string[] }>()

const paginationSettings = reactive({
  page: 1,
  displayPerPage: 500,
})

const showApprovalRequestModal = ref(false)

const approvableInReviewId: Ref<null | string> = ref(null)
const haveMultipleAccounts: ComputedRef<boolean> = computed(() => props.accounts.length > 1)

const tableQuery = graphql(/* GraphQL */ `
  query TablePendingApprovals(
    $page: Int
    $perPage: Int
    $accounts: [ID!]
    $requiresActionUsers: [ID!]
  ) {
    approvalApprovables(
      page: $page
      first: $perPage
      accounts: $accounts
      requiresActionUsers: $requiresActionUsers
    ) {
      paginatorInfo {
        ...PaginatorInfoFragment
        count
        currentPage
        total
      }
      data {
        id
        approval {
          trigger
        }
        approvable {
          ... on Hire {
            ...HireApprovableFragment
          }
        }
        approvalRule {
          id
          approverCount
        }
        approvalStates {
          ...ApprovalStateFragment
        }
      }
    }
  }
`)

const {
  result,
  loading,
  refetch: reloadApprovalApprovables,
} = useQuery(
  tableQuery,
  () => ({
    accounts: props.accounts,
    requiresActionUsers: [currentUser.globalId],
    page: paginationSettings.page,
    perPage: paginationSettings.displayPerPage,
  }),
  {
    clientId: 'publicApiCustomFields',
    debounce: 100,
  },
)

const paginatorInfo = computed(() =>
  useFragment(PaginatorInfoFragment, result.value?.approvalApprovables?.paginatorInfo),
)

function latestStateFromApprovable(approvable: any) {
  return approvable.approvalStates[approvable.approvalStates.length - 1]
}

const tableData = computed(
  () =>
    new TableDataHandler({
      rows: result.value?.approvalApprovables?.data || [],
      columns: [
        {
          title: $t('Last activity'),
          key: 'createdAt',
          class: 'nowrap tw-max-w-fit',
          colClass: { 'tw-max-w-fit tw-w-1 nowrap': true },
          order: 'desc',
          renderUsing: {
            component: markRaw(WdsText),
            componentProps: (row) => ({
              text: useLocaleTimeAgo(latestStateFromApprovable(row).createdAt).value,
              secondary: true,
            }),
          },
          convert(row) {
            return new Date(latestStateFromApprovable(row).createdAt)
          },
        },
        {
          title: $t('Workflow'),
          key: 'approval.trigger',
          class: 'nowrap',
          renderUsing: {
            component: markRaw(ApprovalDisplayForTable),
            componentProps: (row) => ({
              trigger: row.approval.trigger,
              workflowObject: row,
            }),
          },
          searchKeys: ['approvable.worker.name', 'approvable.job.name', 'approval.trigger'],
        },
        {
          title: $t('Step number'),
          key: 'position',
          class: 'nowrap tw-max-w-fit tw-text-center',
          colClass: { 'tw-max-w-fit tw-w-1': true },
          orderAble: false,
          renderUsing: {
            component: markRaw(TdText),
            componentProps: (row) => ({
              html: `<strong>${latestStateFromApprovable(row).approver.position}</strong><span class="tw-text-gray-700">/${row.approvalRule.approverCount}</span>`,
            }),
          },
          convert(row) {
            return latestStateFromApprovable(row).approver.position
          },
        },
        {
          title: $t('Reviewer'),
          key: 'approver',
          class: 'nowrap tw-max-w-fit',
          colClass: { 'tw-max-w-fit tw-w-1': true },
          sort: 'approver.userGroup.name',
          slot: 'Reviewer',
          getFn: (row) => latestStateFromApprovable(row).approver.userGroup.name,
        },
        {
          title: $t('Company'),
          key: 'company.id',
          class: 'nowrap',
          colClass: { 'tw-max-w-fit tw-w-1': true },
          orderAble: false,
          renderUsing: {
            component: markRaw(TdText),
            componentProps: (row) => ({
              html: `
                <strong
                style="display: inline-block; max-width: 300px; text-overflow:ellipsis; overflow:hidden; white-space: nowrap; font-weight: 500;"
                title="${row?.approvable?.company?.name ?? ''}"
              >
                ${row?.approvable?.company?.name ?? ''}
              </strong>`,
            }),
          },
        },
        {
          title: $t('Requested'),
          key: 'requested',
          class: 'nowrap tw-max-w-fit',
          colClass: { 'tw-max-w-fit tw-w-1': true },
          getFn: (row) => latestStateFromApprovable(row).actionedBy.name,
          renderUsing: {
            component: markRaw(WdsText),
            componentProps: (row) => ({
              text: latestStateFromApprovable(row).actionedBy.name,
              subText: useLocaleTimeAgo(latestStateFromApprovable(row).createdAt).value,
            }),
          },
          convert(row) {
            return latestStateFromApprovable(row).actionedBy.name
          },
        },
        {
          title: '',
          slot: 'Action',
          class: 'nowrap tw-max-w-fit',
          colClass: { 'tw-max-w-fit tw-w-1': true },
          key: 'action',
          orderAble: false,
        },
      ].filter((col) => col.key !== 'company.id' || haveMultipleAccounts.value),
    }),
)

function updatePagination(options: any) {
  paginationSettings.displayPerPage = options.perPage
  paginationSettings.page = options.page + 1
}

function openApprovalRequestModal(approvable: any) {
  approvableInReviewId.value = approvable.id
  showApprovalRequestModal.value = true
}
</script>

<style lang="scss" scoped>
.pending-approvals {
  &:deep(.step-number-class) {
    width: 4rem;
  }
}

.icon-inner {
  padding: 5px;
  border-radius: 50%;
  border: 6px solid #f3f0ff;
  background-color: #e8e2ff;
  &:deep(.wds-icon-checkmark) {
    path {
      stroke: #5d36f1;
      stroke-width: 1px;
    }
  }
}
</style>
